import { Box, FormLabel, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { useEffect, useState } from 'react';
import { Controller, Path } from 'react-hook-form';

import { reemaColors } from '@act/toolkit/styles/colors';
import { useMobile } from '@act/utilities/MobileContext';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { useLanguage } from '../../storybook-utilities/useLanguage.hook';
import { BaseControlledInputProps } from '../basicControlledInputProps.types';
import FormFieldWrappers from '../FormFieldWrappers';
import { useFormTracking } from '../hooks/useFormTracking.hook';

export interface ControlledDateProps {
  label: string;
  inputLabel?: string;
  inputType: 'date' | 'datetime-local';
  placeholder?: string;
  isRequired?: boolean;
  valueAsNumber?: boolean;
  minDate?: DateTime;
  maxDate?: DateTime;
  disablePast?: boolean;
  trackingName?: string;
  shouldDisableDate?: (date: DateTime) => boolean;
}

export type ControlledDateFieldProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
> = BaseControlledInputProps<FORM_TYPE, FIELD_NAME> & ControlledDateProps;

const DATE_FORMAT = 'yyyy-MM-dd';

function retypeValue(value: unknown, defaultValue?): DateTime {
  if (typeof value === 'string') {
    return value === '' ? undefined : DateTime.fromFormat(value, DATE_FORMAT);
  }
  if (value instanceof Date) {
    return DateTime.fromJSDate(value);
  }

  // If there isn't a value, but there is a default value, return the default value
  if (defaultValue && !value) {
    return retypeValue(defaultValue);
  }

  return null;
}

export const ControlledDateField = <
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
>({
  inputLabel,
  required,
  disabled,
  disablePast,
  minDate,
  maxDate,
  shouldDisableDate,
  name,
  label,
  defaultValue = undefined,
  control,
  trackingName,
  sensitive = true,
}: ControlledDateFieldProps<FORM_TYPE, FIELD_NAME>) => {
  const [minSetDate, setMinDate] = useState<DateTime | null>();
  const [maxSetDate, setMaxDate] = useState<DateTime | null>();
  const { isMobile } = useMobile();
  const { currentLanguage } = useLanguage();

  const trackingProps = useFormTracking(trackingName, sensitive);

  const selectedLanguage = currentLanguage;
  useEffect(() => {
    // WORK AROUND: Guard against minDate being a number set by storybook.
    // known issue => https://storybook.js.org/docs/api/arg-types
    if (typeof minDate === 'number') {
      setMinDate(DateTime.fromMillis(minDate));
    } else {
      minDate ? setMinDate(minDate) : setMinDate(null);
    }
  }, [minDate]);

  useEffect(() => {
    // WORK AROUND: Guard against maxDate being a number set by storybook.
    // known issue => https://storybook.js.org/docs/api/arg-types
    if (typeof maxDate === 'number') {
      setMaxDate(DateTime.fromMillis(maxDate));
    } else {
      maxDate ? setMaxDate(maxDate) : setMaxDate(null);
    }
  }, [maxDate]);

  return (
    <FormFieldWrappers.FieldWrapper className="controlled-date">
      {inputLabel && (
        <FormLabel
          sx={{
            color: reemaColors.black,
            marginBottom: '8px',
            fontWeight: '500',
          }}
          component="legend"
        >
          {inputLabel}
        </FormLabel>
      )}
      <FormFieldWrappers.InputWrapper>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Controller
            name={name}
            defaultValue={defaultValue}
            rules={{
              ...(required && { required: `${label} is required.` }),
            }}
            disabled={disabled}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <LocalizationProvider
                  dateAdapter={AdapterLuxon}
                  adapterLocale={selectedLanguage}
                >
                  {isMobile ? (
                    <TextField
                      {...trackingProps}
                      type="date"
                      sx={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                      }}
                      value={retypeValue(value, defaultValue)?.toFormat(
                        DATE_FORMAT
                      )}
                      onChange={val => {
                        const selectedDate = DateTime.fromFormat(
                          val.target.value,
                          DATE_FORMAT
                        );
                        // Check if the selected date should be disabled
                        if (
                          shouldDisableDate &&
                          shouldDisableDate(selectedDate)
                        ) {
                          // Don't update the value if the date should be disabled
                          return;
                        }
                        onChange(val.target.value);
                      }}
                      label={label}
                      InputLabelProps={{ shrink: true }}
                      disabled={disabled}
                      inputProps={{
                        min: disablePast
                          ? new Date().toISOString()
                          : minSetDate,
                        max: maxSetDate,
                      }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  ) : (
                    <DatePicker
                      {...trackingProps}
                      minDate={minSetDate}
                      maxDate={maxSetDate}
                      disablePast={disablePast}
                      disabled={disabled}
                      shouldDisableDate={shouldDisableDate}
                      onChange={val => onChange(val?.toFormat(DATE_FORMAT))}
                      value={retypeValue(value, defaultValue)}
                      label={label}
                      sx={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                      }}
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message,
                        },
                      }}
                    />
                  )}
                </LocalizationProvider>
              </Box>
            )}
          />
        </LocalizationProvider>
      </FormFieldWrappers.InputWrapper>
    </FormFieldWrappers.FieldWrapper>
  );
};

export default ControlledDateField;
